---
title: "Trading Psychology Basics: Fear, Greed and FOMO"
slug: trading-psychology-basics
shortTitle: "Trading Psychology Basics"
description: "Why fear, greed and FOMO push traders into poor decisions, how to spot them in your own trading, and the simple rules and alerts that keep emotions in check."
level: beginner
category: trading
course: beginner
order: 13
topics: [psychology]
publishedAt: 2026-10-19
updatedAt: 2026-10-19
featured: false
popular: false
cover: /academy/img/covers/trading-psychology-basics.svg
faqs:
  - q: "Can I remove emotions from trading completely?"
    a: "No, and you do not need to. Fear and greed are normal reactions to risk. The aim is to make your key decisions before you enter a trade, so emotions have less room to change them while the trade is running."
  - q: "What does FOMO mean in trading?"
    a: "FOMO stands for fear of missing out. It is the urge to jump into a move because it is already running and everyone seems to be making money, usually without a plan and often near the end of the move."
  - q: "What is revenge trading?"
    a: "Revenge trading is trying to win back a loss quickly, usually with a bigger position or a trade you would not normally take. It tends to turn one manageable loss into several."
  - q: "How can I tell if I am trading emotionally?"
    a: "Common signs are breaking your own rules, moving a stop loss further away, trading more after a loss, and not being able to explain why you entered before you did. If you notice them, stepping away from the screen is usually the best next move."
  - q: "Does practising on a demo account help with psychology?"
    a: "It helps you learn the mechanics without pressure, which removes one source of stress. Real money feels different, though, so many traders move to small live positions before sizing up."
---

Most people learn the mechanics of trading in a few days. Buy, sell, stop loss, take profit: the buttons are not hard. What takes much longer is learning to press them at the right time, for the right reasons, when real money is moving on the screen. That part is psychology, and it decides more trading results than most beginners expect.

This lesson looks at the three emotions that cause the most damage, **fear**, **greed** and **FOMO** (fear of missing out), shows what each one looks like in practice, and gives you simple rules and platform tools that keep them from making your decisions for you.

<CTA />

<KeyTakeaways>

- **Emotions are normal, not a flaw.** Fear and greed are built-in reactions to risk; the goal is to stop them making decisions for you.
- **Each emotion has tell-tale behaviors.** Cutting winners early, letting losers run, oversizing and chasing are the usual signs.
- **FOMO is a timing problem.** By the time a move feels impossible to miss, a lot of it may be over, and a sensible stop is far away.
- **Rules made in advance beat willpower in the moment.** A fixed risk per trade, a daily loss limit and a TP / SL set at entry do the heavy lifting.

</KeyTakeaways>

## Why trading feels so personal

Trading combines three things that the brain handles badly: money, uncertainty and instant feedback. Your PnL changes every second, in USDT, right under the chart. Each tick feels like a verdict on you.

A few well-known biases make it worse:

- **Loss aversion.** A loss feels worse than an equal gain feels good, so people go to great lengths to avoid taking one, including holding a bad trade far too long.
- **Recency bias.** Your last three trades shape your confidence far more than your last hundred. A short winning streak feels like skill; a short losing streak feels like doom.
- **The need to be right.** Closing a losing trade means admitting a mistake, so it is tempting to wait for the market to "prove you right".

None of this means you are bad at trading. It means you need a process that does not rely on feeling calm.

## Fear: when protecting yourself becomes the problem

Fear is useful in small doses: it is why you use a stop loss at all. Too much of it shows up as:

- Skipping a trade that matches your plan because the last one lost.
- Closing a winner at the first wobble, long before your take profit.
- Holding a loser and hoping it comes back, because closing it makes the loss "real".

The fix is mostly about size and preparation. If a normal loss on one trade would hurt, the position is too big, and fear will take over. Set the stop loss and take profit when you open the trade, so the exits are decided while you are calm.

## Greed: when enough is never enough

Greed is the mirror image. It shows up as:

- Removing a take profit "to let it run", with no new plan for where to exit.
- Doubling the size after a few wins.
- Choosing more leverage than the trade needs, just because the slider allows it.
- Opening more and more positions until you cannot follow them all.

The cure is a fixed amount of risk per trade that does not change with your mood. Changes to an open trade should follow rules you wrote in advance, such as moving the stop loss to your entry once the price has moved a set distance in your favor.

## FOMO: the fear of missing out

FOMO is the urge to jump into a move because it is already running. A coin is trending, a big candle is forming, a market tops the list of movers, and suddenly waiting feels like losing. The problem is mathematical as much as emotional: chasing makes your stop either far away or meaningless.

<Example title="The cost of chasing (illustrative prices)">

You planned to buy ETH/USDT near **3,000** with a stop loss at **2,940**, a risk of 60 per ETH. You were willing to lose **30 USDT**, so your planned size was 30 ÷ 60 = **0.5 ETH**.

The price runs to **3,150** before you act. If you chase it with the same stop:

- Risk per ETH: 3,150 − 2,940 = **210**, three and a half times the plan.
- Keeping 0.5 ETH would risk 0.5 × 210 = **105 USDT** instead of 30.
- Keeping the 30 USDT limit means a size of only 30 ÷ 210 ≈ **0.14 ETH**.

</Example>

Either you risk much more than you planned, or you take a position so small it hardly matters, or you move the stop too close to survive normal swings. The calm alternative is to let it go, or wait for a pullback that fits your plan. There is always another trade.

## Revenge trading: when a loss wants payback

After a painful loss, many traders feel a strong urge to win it back immediately. They size up, lower their standards and trade faster. This is **revenge trading**, and it is one of the quickest ways to turn a small drawdown into a large one, because losses are harder to recover than they look.

<CompareTable caption="Gain needed to recover from a loss">

| Account falls by | From 1,000 USDT to | Gain needed to get back to 1,000 |
| --- | --- | --- |
| 10% | 900 USDT | 100 ÷ 900 = 11.1% |
| 20% | 800 USDT | 200 ÷ 800 = 25.0% |
| 30% | 700 USDT | 300 ÷ 700 = 42.9% |
| 50% | 500 USDT | 500 ÷ 500 = 100% |

</CompareTable>

<Warning>

Increasing your size or leverage to win back a loss raises your risk exactly when your judgment is weakest. With leverage, a second loss at double the size can do more damage than the first. If you notice the urge, stop trading for the day.

</Warning>

When revenge trading becomes a pattern, it is often called **tilt**. [Trading Psychology at Scale](/academy/article/trading-psychology-at-scale) covers tilt, routines and discipline in more depth.

## Guardrails that work better than willpower

You cannot argue with an emotion in the moment, so set up rules beforehand.

1. **Fix your risk per trade in USDT.** Many traders use 1% of the account: on 2,000 USDT, that is 20 USDT. Type it into **Loss (USDT)** under **TP / SL** so the stop is placed for you. [Position Sizing](/academy/article/position-sizing) explains the math.
2. **Set a daily loss limit.** For example, 3% of 2,000 USDT is 60 USDT, so three full losses at 20 USDT ends your day.
3. **Let alerts watch the market.** Under Account → Alerts you can be told when your account equity falls a set percentage below its high for the day, or when a price reaches a level you care about. Then you can close the chart.
4. **Write it down.** Note the plan before each trade and how you felt after it. Patterns appear quickly; [Building a Trading Plan and Journal](/academy/article/trading-plan-and-journal) shows how.
5. **Take a break after big days.** Both large wins and large losses distort judgment for a while.

<ProTip>

Turn your rules into "if-then" sentences: "If I lose three trades today, then I stop." "If the price has already moved past my entry zone, then I skip it." Short, specific rules are much easier to follow under pressure than general intentions.

</ProTip>

For the practical slip-ups these emotions lead to, see [Common Beginner Mistakes and How to Avoid Them](/academy/article/common-beginner-mistakes).

## Conclusion

Fear, greed and FOMO are not signs that you are unsuited to trading; they are signs that you are human. Fear cuts winners and holds losers, greed oversizes, FOMO chases, and revenge trading compounds all three. You will not switch them off, but you can take decisions away from them: fix your risk per trade, set your TP / SL when you enter, keep a daily loss limit, let alerts do the watching, and keep a journal. A good process makes calm decisions possible even on days when you do not feel calm.
